import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, Tabs, Tab, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { getAllCommissionProgress } from '../../api/commissionProgressApi';
import { useUser } from '../../context/userContext';
import { handlePriceWithRM } from '../../utils/priceUtils';

dayjs.extend(quarterOfYear);

const FONT = "'Poppins', sans-serif";
const TYPE_COLORS = { PSC: '#1A8CFF', DOC: '#10B981', TOC: '#F59E0B' };
const STATUS_COLORS = { Completed: '#10B981', Partial: '#F59E0B', Pending: '#94A3B8', Voided: '#EF4444' };
const PIE_COLORS = ['#1A8CFF', '#F59E0B', '#10B981', '#6366F1', '#EF4444', '#8B5CF6'];
const WEBBEY_TYPE = 'Webbey Commission';
const AGENT_TYPES = ['PSC', 'DOC', 'TOC'];

const DATE_PRESETS = [
  { label: 'This Month', start: () => dayjs().startOf('month'), end: () => dayjs() },
  { label: 'Last Month', start: () => dayjs().subtract(1, 'month').startOf('month'), end: () => dayjs().subtract(1, 'month').endOf('month') },
  { label: 'This Quarter', start: () => dayjs().startOf('quarter'), end: () => dayjs() },
  { label: 'This Year', start: () => dayjs().startOf('year'), end: () => dayjs() },
  { label: 'All Time', start: () => null, end: () => null },
];

const totalPaidOf = (cd) => (cd?.firstPayment || 0) + (cd?.secondPayment || 0)
  + (cd?.repayment || 0) + (cd?.repayment2 || 0) + (cd?.repayment3 || 0) + (cd?.repayment4 || 0) + (cd?.repayment5 || 0);

const statusOf = (cd) => {
  if (!cd) return 'Pending';
  if (cd.paymentStatus === 'Voided' || cd.paymentStatus === 'Void') return 'Voided';
  const total = cd.commPay || 0;
  const paid = totalPaidOf(cd);
  if (total <= 0) return 'Pending';
  if (paid >= total - 0.01) return 'Completed';
  if (paid > 0) return 'Partial';
  return 'Pending';
};

const StatCard = ({ label, value, sub, borderColor }) => (
  <Card sx={{ borderLeft: `4px solid ${borderColor}`, borderRadius: '14px' }}>
    <CardContent sx={{ p: '14px !important', '&:last-child': { pb: '14px !important' } }}>
      <Typography sx={{ fontSize: '0.7rem', color: '#64748B', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px', fontFamily: FONT }}>{label}</Typography>
      <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#0F172A', mt: 0.5, fontFamily: FONT }}>{value}</Typography>
      {sub && <Typography sx={{ fontSize: '0.72rem', color: '#64748B', mt: 0.25, fontFamily: FONT }}>{sub}</Typography>}
    </CardContent>
  </Card>
);

const SectionTitle = ({ title, subtitle, color = '#0F172A' }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color, fontFamily: FONT, lineHeight: 1.2 }}>{title}</Typography>
    {subtitle && <Typography sx={{ fontSize: '0.78rem', color: '#64748B', fontFamily: FONT, mt: 0.25 }}>{subtitle}</Typography>}
  </Box>
);

export default function WebbeyReport() {
  const { user } = useUser();
  const navigate = useNavigate();
  const isAdmin = user?.roleRights?.includes('AllSalesCommList') || user?.roleRights?.includes('WebbeyCommission');

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState(dayjs());
  const [activePreset, setActivePreset] = useState('This Month');
  const [tabIndex, setTabIndex] = useState(0);

  const fetchData = useCallback(async () => {
    if (!isAdmin) { setLoading(false); return; }
    try {
      const res = await getAllCommissionProgress();
      setRecords(res.data || []);
    } catch (err) { console.error('Error:', err); }
    finally { setLoading(false); }
  }, [isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePreset = (p) => {
    setActivePreset(p.label);
    setStartDate(p.start());
    setEndDate(p.end());
  };

  const dateFiltered = useMemo(() => {
    if (!startDate && !endDate) return records;
    return records.filter(r => {
      const dep = r.salesDetailsForCommissionProgress?.depositOn;
      if (!dep) return false;
      const date = dayjs(dep);
      if (startDate && date.isBefore(startDate, 'day')) return false;
      if (endDate && date.isAfter(endDate, 'day')) return false;
      return true;
    });
  }, [records, startDate, endDate]);

  const agentRecords = useMemo(() =>
    dateFiltered.filter(r => AGENT_TYPES.includes(r.commissionProgressDto?.commPayType)), [dateFiltered]);

  const webbeyRecords = useMemo(() =>
    dateFiltered.filter(r => r.commissionProgressDto?.commPayType === WEBBEY_TYPE), [dateFiltered]);

  /* ─── Combined top-line cards ─── */
  const agentTotal = agentRecords.reduce((s, r) => s + (r.commissionProgressDto?.commPay || 0), 0);
  const agentPaid = agentRecords.reduce((s, r) => s + totalPaidOf(r.commissionProgressDto), 0);
  const agentOutstanding = Math.max(0, agentTotal - agentPaid);
  const webbeyTotal = webbeyRecords.reduce((s, r) => s + (r.commissionProgressDto?.commPay || 0), 0);
  const webbeyPaid = webbeyRecords.reduce((s, r) => s + totalPaidOf(r.commissionProgressDto), 0);
  const webbeyOutstanding = Math.max(0, webbeyTotal - webbeyPaid);
  const netCompanyExposure = agentOutstanding;
  const netCompanyEarned = webbeyPaid;

  /* ─── Total commission charts (PSC/DOC/TOC) ─── */
  const agentByMonth = useMemo(() => {
    const map = {};
    agentRecords.forEach(r => {
      const dep = r.salesDetailsForCommissionProgress?.depositOn;
      if (!dep) return;
      const key = dayjs(dep).format('MMM YYYY');
      if (!map[key]) map[key] = { name: key, PSC: 0, DOC: 0, TOC: 0 };
      const t = r.commissionProgressDto?.commPayType;
      if (t && map[key][t] !== undefined) map[key][t] += r.commissionProgressDto?.commPay || 0;
    });
    return Object.values(map).sort((a, b) => dayjs(a.name, 'MMM YYYY').valueOf() - dayjs(b.name, 'MMM YYYY').valueOf());
  }, [agentRecords]);

  const agentByType = useMemo(() => {
    const map = {};
    agentRecords.forEach(r => {
      const t = r.commissionProgressDto?.commPayType || 'Unknown';
      map[t] = (map[t] ?? 0) + (r.commissionProgressDto?.commPay || 0);
    });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [agentRecords]);

  const topAgents = useMemo(() => {
    const map = {};
    agentRecords.forEach(r => {
      const cd = r.commissionProgressDto;
      const id = cd?.paymentTo;
      if (!id) return;
      if (!map[id]) map[id] = { paymentTo: id, name: cd.paymentToName || 'Unknown', total: 0, paid: 0, count: 0, psc: 0, doc: 0, toc: 0 };
      const cp = cd.commPay || 0;
      map[id].total += cp;
      map[id].paid += totalPaidOf(cd);
      map[id].count += 1;
      if (cd.commPayType === 'PSC') map[id].psc += cp;
      else if (cd.commPayType === 'DOC') map[id].doc += cp;
      else if (cd.commPayType === 'TOC') map[id].toc += cp;
    });
    return Object.values(map).sort((a, b) => b.total - a.total).slice(0, 10);
  }, [agentRecords]);

  /* ─── Webbey overriding charts ─── */
  const webbeyByMonth = useMemo(() => {
    const map = {};
    webbeyRecords.forEach(r => {
      const dep = r.salesDetailsForCommissionProgress?.depositOn;
      if (!dep) return;
      const key = dayjs(dep).format('MMM YYYY');
      map[key] = (map[key] ?? 0) + (r.commissionProgressDto?.commPay || 0);
    });
    return Object.entries(map)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => dayjs(a.name, 'MMM YYYY').valueOf() - dayjs(b.name, 'MMM YYYY').valueOf());
  }, [webbeyRecords]);

  const webbeyByStatus = useMemo(() => {
    const map = { Completed: 0, Partial: 0, Pending: 0, Voided: 0 };
    webbeyRecords.forEach(r => { map[statusOf(r.commissionProgressDto)] = (map[statusOf(r.commissionProgressDto)] ?? 0) + 1; });
    return Object.entries(map).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value }));
  }, [webbeyRecords]);

  const webbeyByAgent = useMemo(() => {
    const map = {};
    webbeyRecords.forEach(r => {
      const sd = r.salesDetailsForCommissionProgress;
      const name = sd?.agentName ?? 'Unknown';
      if (!map[name]) map[name] = { name, sales: 0, comm: 0, paid: 0 };
      map[name].sales++;
      map[name].comm += r.commissionProgressDto?.commPay ?? 0;
      map[name].paid += totalPaidOf(r.commissionProgressDto);
    });
    return Object.values(map).sort((a, b) => b.comm - a.comm);
  }, [webbeyRecords]);

  const datePickerSx = { width: 150, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily: FONT, fontSize: '0.85rem' } };

  if (loading) return <div>Loading...</div>;

  if (!isAdmin) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography sx={{ fontFamily: FONT, color: '#64748B' }}>You don't have access to this report.</Typography>
      </Box>
    );
  }

  return (
    <div>
      <Box className="no-print" sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button variant="contained" size="small" startIcon={<PrintIcon />} onClick={() => window.print()}
          sx={{ fontFamily: FONT, textTransform: 'none', borderRadius: '8px', background: 'linear-gradient(135deg, #10B981, #059669)', boxShadow: 'none', fontSize: '0.78rem', fontWeight: 600 }}>
          Print / Save as PDF
        </Button>
      </Box>

      {/* ===== Header ===== */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', fontFamily: FONT }}>Webbey Report</h2>
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748B', fontFamily: FONT }}>Total Commission (agent liability) + Webbey Overriding (company profit) · filtered by deposit date</p>
        </div>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <DatePicker value={startDate} onChange={v => v && (setStartDate(v), setActivePreset(null))} format="DD/MM/YYYY"
            slotProps={{ textField: { size: 'small', sx: datePickerSx } }} />
          <Typography sx={{ color: '#64748B', fontSize: '0.85rem' }}>to</Typography>
          <DatePicker value={endDate} onChange={v => v && (setEndDate(v), setActivePreset(null))} format="DD/MM/YYYY"
            slotProps={{ textField: { size: 'small', sx: datePickerSx } }} />
        </Box>
      </Box>

      {/* Date presets */}
      <Box sx={{ display: 'flex', gap: 0.75, mb: 3, flexWrap: 'wrap' }}>
        {DATE_PRESETS.map(p => (
          <Chip key={p.label} label={p.label} size="small" onClick={() => handlePreset(p)}
            sx={{
              fontFamily: FONT, fontSize: '0.75rem',
              fontWeight: activePreset === p.label ? 600 : 400,
              backgroundColor: activePreset === p.label ? '#1A8CFF' : '#F1F5F9',
              color: activePreset === p.label ? '#fff' : '#475569',
              '&:hover': { backgroundColor: activePreset === p.label ? '#1574D4' : '#E2E8F0' },
            }} />
        ))}
      </Box>

      {/* ===== Combined headline cards ===== */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 3 }}>
        <StatCard label="Total Commission (Agent Liability)" value={handlePriceWithRM(agentTotal)} sub={`${agentRecords.length} records · PSC + DOC + TOC`} borderColor="#1A8CFF" />
        <StatCard label="Total Webbey Overriding (Profit)" value={handlePriceWithRM(webbeyTotal)} sub={`${webbeyRecords.length} records · Company revenue`} borderColor="#0F766E" />
        <StatCard label="Net Outstanding to Agents" value={handlePriceWithRM(netCompanyExposure)} sub="Yet to pay out" borderColor="#F59E0B" />
        <StatCard label="Net Webbey Received" value={handlePriceWithRM(netCompanyEarned)} sub="Already collected" borderColor="#10B981" />
      </Box>

      {/* ===== Tabs ===== */}
      <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)}
        sx={{ mb: 2, borderBottom: '1px solid #E2E8F0', '& .MuiTab-root': { fontFamily: FONT, textTransform: 'none', fontSize: '0.85rem', fontWeight: 600 } }}>
        <Tab label="🧑 Total Commission" />
        <Tab label="🏢 Webbey Overriding" />
      </Tabs>

      {tabIndex === 0 && (<>
      <SectionTitle title="🧑 Total Commission" subtitle="PSC + DOC + TOC — what we owe agents" />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
        <StatCard label="Total Comm" value={handlePriceWithRM(agentTotal)} sub={`${agentRecords.length} records`} borderColor="#1A8CFF" />
        <StatCard label="Paid" value={handlePriceWithRM(agentPaid)} sub={agentTotal > 0 ? `${Math.round(agentPaid/agentTotal*100)}% of total` : '—'} borderColor="#10B981" />
        <StatCard label="Outstanding" value={handlePriceWithRM(agentOutstanding)} sub="Yet to pay out" borderColor="#F59E0B" />
        <StatCard label="Average / Record" value={handlePriceWithRM(agentRecords.length > 0 ? Math.round(agentTotal / agentRecords.length) : 0)} sub="Per commission" borderColor="#6366F1" />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 1.5, mb: 2 }}>
        <Card sx={{ borderRadius: '14px' }}>
          <CardContent>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 2, fontFamily: FONT }}>Commission by Month</Typography>
            {agentByMonth.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={agentByMonth} margin={{ left: 10, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" style={{ fontSize: '0.7rem', fontFamily: FONT }} />
                  <YAxis tickFormatter={v => `RM ${(v / 1000).toFixed(0)}k`} style={{ fontSize: '0.7rem', fontFamily: FONT }} />
                  <Tooltip formatter={v => handlePriceWithRM(v)} />
                  <Legend />
                  <Bar dataKey="PSC" stackId="a" fill={TYPE_COLORS.PSC} />
                  <Bar dataKey="DOC" stackId="a" fill={TYPE_COLORS.DOC} />
                  <Bar dataKey="TOC" stackId="a" fill={TYPE_COLORS.TOC} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No data</Typography>}
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: '14px' }}>
          <CardContent>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 2, fontFamily: FONT }}>By Type</Typography>
            {agentByType.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={agentByType} cx="50%" cy="50%" innerRadius={55} outerRadius={80} dataKey="value"
                    label={({ name, value }) => `${name}: ${handlePriceWithRM(value)}`} labelLine={false}>
                    {agentByType.map((entry, i) => (
                      <Cell key={entry.name} fill={TYPE_COLORS[entry.name] ?? PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={v => handlePriceWithRM(v)} />
                </PieChart>
              </ResponsiveContainer>
            ) : <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No data</Typography>}
          </CardContent>
        </Card>
      </Box>

      <Card sx={{ borderRadius: '14px', mb: 4 }}>
        <CardContent>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 0.5, fontFamily: FONT }}>Top 10 Recipients</Typography>
          <Typography sx={{ fontSize: '0.72rem', color: '#64748B', mb: 2, fontFamily: FONT }}>Click for that agent's full breakdown</Typography>
          {topAgents.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontFamily: FONT, fontSize: '0.75rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #E2E8F0' } }}>
                    <TableCell>#</TableCell>
                    <TableCell>Recipient</TableCell>
                    <TableCell align="right">Records</TableCell>
                    <TableCell align="right">PSC</TableCell>
                    <TableCell align="right">DOC</TableCell>
                    <TableCell align="right">TOC</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Paid</TableCell>
                    <TableCell align="right">Outstanding</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topAgents.map((a, i) => (
                    <TableRow key={a.paymentTo} hover
                      onClick={() => navigate(`/reports/commission/agent/${a.paymentTo}`)}
                      sx={{ cursor: 'pointer', '& td': { fontFamily: FONT, fontSize: '0.8rem', color: '#0F172A', borderBottom: '1px solid #F1F5F9' }, '&:hover': { backgroundColor: '#F0F7FF' } }}>
                      <TableCell sx={{ fontWeight: 600, color: '#64748B' }}>{i + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 500, color: '#1A8CFF' }}>{a.name}</TableCell>
                      <TableCell align="right">{a.count}</TableCell>
                      <TableCell align="right">{handlePriceWithRM(a.psc)}</TableCell>
                      <TableCell align="right">{handlePriceWithRM(a.doc)}</TableCell>
                      <TableCell align="right">{handlePriceWithRM(a.toc)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{handlePriceWithRM(a.total)}</TableCell>
                      <TableCell align="right" sx={{ color: '#10B981' }}>{handlePriceWithRM(a.paid)}</TableCell>
                      <TableCell align="right" sx={{ color: '#F59E0B' }}>{handlePriceWithRM(Math.max(0, a.total - a.paid))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No data</Typography>}
        </CardContent>
      </Card>

      </>)}

      {tabIndex === 1 && (<>
      <SectionTitle title="🏢 Webbey Overriding" subtitle="Company's commission cut — net profit per sale" color="#0F766E" />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
        <StatCard label="Total Webbey" value={handlePriceWithRM(webbeyTotal)} sub={`${webbeyRecords.length} records`} borderColor="#0F766E" />
        <StatCard label="Received" value={handlePriceWithRM(webbeyPaid)} sub={webbeyTotal > 0 ? `${Math.round(webbeyPaid/webbeyTotal*100)}% of total` : '—'} borderColor="#10B981" />
        <StatCard label="Pending" value={handlePriceWithRM(webbeyOutstanding)} sub="Yet to receive" borderColor="#F59E0B" />
        <StatCard label="Average / Record" value={handlePriceWithRM(webbeyRecords.length > 0 ? Math.round(webbeyTotal / webbeyRecords.length) : 0)} sub="Per sale" borderColor="#8B5CF6" />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 1.5, mb: 2 }}>
        <Card sx={{ borderRadius: '14px' }}>
          <CardContent>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 2, fontFamily: FONT }}>Webbey by Month</Typography>
            {webbeyByMonth.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={webbeyByMonth} margin={{ left: 10, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" style={{ fontSize: '0.7rem', fontFamily: FONT }} />
                  <YAxis tickFormatter={v => `RM ${(v / 1000).toFixed(0)}k`} style={{ fontSize: '0.7rem', fontFamily: FONT }} />
                  <Tooltip formatter={v => handlePriceWithRM(v)} />
                  <Bar dataKey="value" fill="#0F766E" radius={[4, 4, 0, 0]} barSize={32} />
                </BarChart>
              </ResponsiveContainer>
            ) : <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No data</Typography>}
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: '14px' }}>
          <CardContent>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 2, fontFamily: FONT }}>Payment Status</Typography>
            {webbeyByStatus.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={webbeyByStatus} cx="50%" cy="50%" innerRadius={55} outerRadius={80} dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`} labelLine={false}>
                    {webbeyByStatus.map((entry, i) => (
                      <Cell key={entry.name} fill={STATUS_COLORS[entry.name] ?? PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No data</Typography>}
          </CardContent>
        </Card>
      </Box>

      <Card sx={{ borderRadius: '14px' }}>
        <CardContent>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 2, fontFamily: FONT }}>Webbey by Sales Agent</Typography>
          {webbeyByAgent.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontFamily: FONT, fontSize: '0.75rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #E2E8F0' } }}>
                    <TableCell>Agent</TableCell>
                    <TableCell align="right">Sales</TableCell>
                    <TableCell align="right">Webbey Comm</TableCell>
                    <TableCell align="right">Received</TableCell>
                    <TableCell align="right">Pending</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {webbeyByAgent.map(a => (
                    <TableRow key={a.name} hover sx={{ '& td': { fontFamily: FONT, fontSize: '0.8rem', color: '#0F172A', borderBottom: '1px solid #F1F5F9' } }}>
                      <TableCell sx={{ fontWeight: 500 }}>{a.name}</TableCell>
                      <TableCell align="right">{a.sales}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{handlePriceWithRM(a.comm)}</TableCell>
                      <TableCell align="right" sx={{ color: '#10B981' }}>{handlePriceWithRM(a.paid)}</TableCell>
                      <TableCell align="right" sx={{ color: '#F59E0B' }}>{handlePriceWithRM(Math.max(0, a.comm - a.paid))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No data</Typography>}
        </CardContent>
      </Card>
      </>)}
    </div>
  );
}
